import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackLinkClick } from "../services/linkClick";
import { trackClubRef } from "../services/club";
import { getAffiliateClientId } from "../services/affiliate";
import { useGlobalState } from "../GlobalStates";

// Pure: extract { ref, refId } from a URL query string, or null if no ref.
export const readRefParams = (search) => {
  const params = new URLSearchParams(search);
  const ref = params.get("ref");
  if (!ref) return null;
  return { ref, refId: params.get("ref_id") || "" };
};

// Club links carry ?ref=<club handle> (Sotravel Club, Oct 2026). The latest one
// counts (a later ref to another club replaces it), so only an identical repeat
// in the same tab is skipped; the BE ignores refs that aren't a club handle.
// Logged out, the visit is kept by browser id and claimed after login.
const sendClubRef = (ref) => {
  const lastKey = "club_ref_last_sent";
  try {
    if (sessionStorage.getItem(lastKey) === ref) return;
    sessionStorage.setItem(lastKey, ref);
  } catch {
    // ignore
  }
  let clientId = null;
  try {
    clientId = getAffiliateClientId();
  } catch {
    // storage unavailable
  }
  trackClubRef(ref, clientId).then((res) => {
    try {
      if (res?.recorded && !localStorage.getItem("JWT_TOKEN") && clientId) {
        localStorage.setItem("club_visit_client_id", clientId);
      }
    } catch {
      // ignore
    }
  });
};

// Claims logged-out club visits (club page / ?ref=) as soon as the visitor has an
// account in this tab — also after logging in mid-session, not only on app load.
const useClaimClubVisits = () => {
  const [user] = useGlobalState("user");
  useEffect(() => {
    if (!user?.id) return;
    let pending = null;
    try {
      pending = localStorage.getItem("club_visit_client_id");
    } catch {
      return;
    }
    if (!pending) return;
    import("../services/club").then(({ claimClubVisits }) =>
      claimClubVisits(pending)
        .then(() => localStorage.removeItem("club_visit_client_id"))
        .catch(() => {})
    );
  }, [user?.id]);
};

// Global, path-independent tracker. Mounted once at the app root (inside <Router>,
// outside <Routes>), so it fires on whatever path the visitor lands on as long as
// a `ref` query param is present. Server-side dedup is the real guard; the
// sessionStorage check just avoids redundant network calls within a tab session.
export default function LinkClickTracker() {
  const location = useLocation();
  useClaimClubVisits();

  useEffect(() => {
    const parsed = readRefParams(location.search);
    if (parsed?.ref) sendClubRef(parsed.ref.trim().toLowerCase());
  }, [location.search]);

  useEffect(() => {
    const parsed = readRefParams(location.search);
    if (!parsed) return;

    const dedupeKey = `link_click_sent:${encodeURIComponent(parsed.ref)}:${encodeURIComponent(
      parsed.refId
    )}`;
    try {
      if (sessionStorage.getItem(dedupeKey) === "1") return;
    } catch {
      // ignore privacy-mode / quota errors — fall through and send
    }

    trackLinkClick(parsed.ref, parsed.refId).catch(() => {
      // fire-and-forget; ignore network errors
    });

    try {
      sessionStorage.setItem(dedupeKey, "1");
    } catch {
      // ignore
    }
  }, [location.search]);

  return null;
}
